import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Typography, Divider, Spin, Space } from 'antd';
import { NotificationCard, BroadcastMessageForm } from './components';
import { fetchNotificationSettings, updateNotificationSetting } from './utils/fetch';
import { changePageTitle } from '../../utils/title';

const { Title, Text } = Typography;

/**
 * Главная страница управления рассылками
 */
const Notifications = ({ userPermission }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState({});

  useEffect(() => {
    changePageTitle('Управление рассылками');
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await fetchNotificationSettings();
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (type, isEnabled) => {
    setToggleLoading((prev) => ({ ...prev, [type]: true }));
    try {
      await updateNotificationSetting(type, isEnabled);
      // Обновить локальное состояние
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.type === type ? { ...notif, isEnabled } : notif
        )
      );
    } catch (error) {
      console.error('Error toggling notification:', error);
    } finally {
      setToggleLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  // Обработчик переключения массовой рассылки
  const handleBroadcastToggle = (isEnabled) => {
    setNotifications((prev) => {
      const hasBroadcast = prev.find(n => n.type === 'BROADCAST_ENABLED');
      if (hasBroadcast) {
        return prev.map(n =>
          n.type === 'BROADCAST_ENABLED' ? { ...n, isEnabled } : n
        );
      } else {
        // Если настройки нет, добавить её
        return [...prev, {
          type: 'BROADCAST_ENABLED',
          name: 'Массовая рассылка',
          description: 'Включение/выключение возможности массовой рассылки',
          phoneNumber: null,
          category: 'SYSTEM',
          isEnabled,
          functionName: null
        }];
      }
    });
  };

  // Разделить уведомления по категориям
  const departmentNotifications = notifications.filter(
    (n) => n.category === 'DEPARTMENT'
  );
  const clientNotifications = notifications.filter(
    (n) => n.category === 'CLIENT'
  );
  const documentNotifications = notifications.filter(
    (n) => n.category === 'DOCUMENT'
  );

  // Fallback данные для системных настроек сверки
  const defaultSystemNotifications = [
    {
      type: 'RECONCILIATION_NOTIFY_SUCCESS',
      name: 'Уведомления о сверке данных',
      description: 'Отправлять отчёт если расхождений не найдено. При расхождениях отправляется всегда.',
      category: 'SYSTEM',
      isEnabled: false,
      phoneNumber: null
    }
  ];

  // Объединить API данные с fallback (приоритет у API)
  const apiSystemNotifications = notifications.filter(
    (n) => n.category === 'SYSTEM' && n.type !== 'BROADCAST_ENABLED'
  );
  const systemNotifications = defaultSystemNotifications.map(defaultNotif => {
    const fromApi = apiSystemNotifications.find(n => n.type === defaultNotif.type);
    return fromApi || defaultNotif;
  });

  return (
    <Layout style={{ padding: '24px', minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Заголовок страницы */}
      <Card
        bordered={false}
        style={{ marginBottom: '24px', borderRadius: '8px' }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Управление рассылками
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Управление автоматическими WhatsApp уведомлениями для кабинетов и клиентов
        </Text>
      </Card>

      <Spin spinning={loading}>
        {/* Секция: Уведомления кабинетов */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ marginBottom: '16px' }}>
            Уведомления кабинетов (отделов)
          </Title>
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            Автоматические уведомления о новых заявках для производства, сборки, доставки и монтажа
          </Text>
          <Row gutter={[16, 16]}>
            {departmentNotifications.map((notification) => (
              <Col xs={24} sm={12} lg={6} key={notification.type}>
                <NotificationCard
                  notification={notification}
                  onToggle={handleToggle}
                  loading={toggleLoading[notification.type]}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Секция: Уведомления клиентам */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ marginBottom: '16px' }}>
            Уведомления клиентам
          </Title>
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            Автоматические сообщения клиентам на различных этапах заказа
          </Text>
          <Row gutter={[16, 16]}>
            {clientNotifications.map((notification) => (
              <Col xs={24} sm={12} lg={8} key={notification.type}>
                <NotificationCard
                  notification={notification}
                  onToggle={handleToggle}
                  loading={toggleLoading[notification.type]}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Секция: Управление документами */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ marginBottom: '16px' }}>
            Управление рассылкой документов
          </Title>
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            Управление автоматической отправкой документов клиентам
          </Text>
          <Row gutter={[16, 16]}>
            {documentNotifications.map((notification) => (
              <Col xs={24} sm={12} lg={8} key={notification.type}>
                <NotificationCard
                  notification={notification}
                  onToggle={handleToggle}
                  loading={toggleLoading[notification.type]}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Секция: Системные настройки */}
        <div style={{ marginBottom: '32px' }}>
          <Title level={3} style={{ marginBottom: '16px' }}>
            Системные настройки
          </Title>
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            Управление уведомлениями о результатах сверки данных
          </Text>
          <Row gutter={[16, 16]}>
            {systemNotifications.map((notification) => (
              <Col xs={24} sm={12} lg={8} key={notification.type}>
                <NotificationCard
                  notification={notification}
                  onToggle={handleToggle}
                  loading={toggleLoading[notification.type]}
                />
              </Col>
            ))}
          </Row>
        </div>

        <Divider />

        {/* Секция: Массовая рассылка */}
        <div>
          <BroadcastMessageForm
            departments={departmentNotifications}
            notifications={notifications}
            onBroadcastToggle={handleBroadcastToggle}
          />
        </div>
      </Spin>
    </Layout>
  );
};

export default Notifications;

